import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject,
  signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { DropdownItem } from '@view/shared/molecules/dropdown/dropdown.component';
import { FAMILY_MEMBERS_PORT } from '@domain/service/family-members.port';
import {
  CreateFamilyMembersRequest,
  CreateFamilyMembersResponse,
  CreateFamilyMemberData,
  FamilyExamType
} from '@model/family-member/create-family-members.model';
import { ToastService } from '@view/shared/molecules/toast/service/toast.service';
import { ToastTypeEnum } from '@view/shared/molecules/toast/toast-type-enum';
import {
  colombianPhoneValidator,
  emailFormatValidator,
  maxDateValidator
} from '@view/shared/validators/form-validators';

export interface AffecteeOrderData {
  siteId: string;
  priorityId: string;
  laboratoryId: string;
  diagnosisId: string;
  specialistId: string;
  providerId: string;
  companyId: string;
  regionalId: string;
}

@Component({
  selector: 'app-family-members-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './family-members-modal.component.html',
  styleUrls: ['./family-members-modal.component.scss']
})
export class FamilyMembersModalComponent implements OnInit, OnChanges {
  private readonly fb = inject(FormBuilder);
  private readonly familyMembersService = inject(FAMILY_MEMBERS_PORT);
  private readonly toastService = inject(ToastService);

  // Inputs
  @Input() lgBase = '';
  @Input() examType: FamilyExamType = 'TRIO';
  @Input() orderId = '';
  @Input() placerOrderNumber = '';
  @Input() affecteeOrderData?: AffecteeOrderData;
  @Input() identifierTypes: DropdownItem[] = [];
  @Input() specimenTypes: DropdownItem[] = [];
  @Input() specimenContainers: DropdownItem[] = [];
  @Input() priorities: DropdownItem[] = [];
  @Input() companies: DropdownItem[] = [];
  @Input() providers: DropdownItem[] = [];
  @Input() regions: DropdownItem[] = [];
  @Input() sites: DropdownItem[] = [];
  @Input() laboratories: DropdownItem[] = [];
  @Input() diagnoses: DropdownItem[] = [];
  @Input() specialists: DropdownItem[] = [];
  @Input() examTypes: DropdownItem[] = [];
  @Input() examTypeId = '';
  @Input() causeReprocessOptions: DropdownItem[] = [];
  @Input() statusReprocessOptions: DropdownItem[] = [];

  @Output() saved = new EventEmitter<CreateFamilyMembersResponse>();
  @Output() closed = new EventEmitter<void>();

  isOpen = signal(false);
  isLoading = signal(false);

  fatherForm!: FormGroup;
  motherForm!: FormGroup;

  sexOptions: DropdownItem[] = [
    { value: 'M', label: 'Masculino' },
    { value: 'F', label: 'Femenino' }
  ];

  ngOnInit(): void {
    this.initForms();
  }

  private initForms(): void {
    this.fatherForm = this.createFamilyMemberForm('M');
    this.motherForm = this.createFamilyMemberForm('F');
    this.setupUppercaseTransformation(this.fatherForm);
    this.setupUppercaseTransformation(this.motherForm);
  }

  private setupUppercaseTransformation(form: FormGroup): void {
    const fieldsToUppercase = [
      'givenName', 'middleName', 'familyName', 'maternalFamilyName',
      'maritalStatus', 'userDataDescription', 'clinicalHistory',
      'descriptionDataSample', 'descriptionDataExam'
    ];

    fieldsToUppercase.forEach((fieldName) => {
      form.get(fieldName)?.valueChanges.subscribe((value: string | null) => {
        if (value && typeof value === 'string' && value !== value.toUpperCase()) {
          form.get(fieldName)?.setValue(value.toUpperCase(), { emitEvent: false });
        }
      });
    });
  }

  private createFamilyMemberForm(preselectedSex: string): FormGroup {
    return this.fb.group({
      identifierTypeCode: ['', [Validators.required]],
      idNumber: ['', [Validators.required]],
      givenName: ['', [Validators.required]],
      middleName: [''],
      familyName: ['', [Validators.required]],
      maternalFamilyName: [''],
      administrativeSex: [preselectedSex, [Validators.required]],
      birthDate: ['', [Validators.required, maxDateValidator()]],
      maritalStatus: [''],
      race: [''],
      emailAddress: ['', [emailFormatValidator()]],
      telephoneNumber: ['', [Validators.required, colombianPhoneValidator()]],
      streetAddress: [''],
      city: [''],
      state: [''],
      country: [''],
      specimenId: [''],
      specimenTypeId: ['', [Validators.required]],
      specimenContainerId: ['', [Validators.required]],
      priorityId: ['', [Validators.required]],
      examTypeSelected: ['', [Validators.required]],
      caseNumber: [''],
      companyId: [''],
      providerId: [''],
      regionalId: [''],
      siteId: [''],
      laboratoryId: [''],
      diagnosisId: [''],
      specialistId: [''],
      userDataDescription: [''],
      clinicalHistory: [''],
      descriptionDataSample: [''],
      descriptionDataExam: [''],
      consentForUseOfSamples: [false],
      acmg: [false],
      mlpa: [false],
      prioritization: [false],
      isReprocess: [false],
      causeReprocessId: [''],
      statusReprocessId: [''],
      reprocessDescription: ['']
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['examTypeId'] &&
      this.examTypeId &&
      this.fatherForm &&
      this.motherForm
    ) {
      this.fatherForm.patchValue({ examTypeSelected: this.examTypeId });
      this.motherForm.patchValue({ examTypeSelected: this.examTypeId });
    }
  }

  open(): void {
    this.isOpen.set(true);

    if (this.examTypeId) {
      this.fatherForm.patchValue({ examTypeSelected: this.examTypeId });
      this.motherForm.patchValue({ examTypeSelected: this.examTypeId });
    }

    if (this.affecteeOrderData) {
      const orderValues = {
        siteId: this.affecteeOrderData.siteId,
        priorityId: this.affecteeOrderData.priorityId,
        laboratoryId: this.affecteeOrderData.laboratoryId,
        diagnosisId: this.affecteeOrderData.diagnosisId,
        specialistId: this.affecteeOrderData.specialistId,
        providerId: this.affecteeOrderData.providerId,
        companyId: this.affecteeOrderData.companyId,
        regionalId: this.affecteeOrderData.regionalId
      };
      this.fatherForm.patchValue(orderValues);
      this.motherForm.patchValue(orderValues);
    }
  }

  close(): void {
    this.isOpen.set(false);
    this.closed.emit();
  }

  get modalTitle(): string {
    if (this.examType === 'FUTUROS_PADRES') {
      return 'Ingreso de Pareja - Futuros Padres';
    }
    return `Ingreso de Familiares - ${this.examType}`;
  }

  get fatherSectionTitle(): string {
    if (this.examType === 'FUTUROS_PADRES') {
      return 'Datos del Futuro Padre';
    }
    return 'Datos del Padre';
  }

  get motherSectionTitle(): string {
    if (this.examType === 'FUTUROS_PADRES') {
      return 'Datos de la Futura Madre';
    }
    return 'Datos de la Madre';
  }

  copyFromFatherToMother(): void {
    const fatherValue = this.fatherForm.value as Record<string, unknown>;

    this.motherForm.patchValue({
      emailAddress: fatherValue['emailAddress'],
      telephoneNumber: fatherValue['telephoneNumber'],
      streetAddress: fatherValue['streetAddress'],
      city: fatherValue['city'],
      state: fatherValue['state'],
      country: fatherValue['country'],
      specimenTypeId: fatherValue['specimenTypeId'],
      specimenContainerId: fatherValue['specimenContainerId'],
      priorityId: fatherValue['priorityId'],
      companyId: fatherValue['companyId'],
      providerId: fatherValue['providerId'],
      regionalId: fatherValue['regionalId'],
      siteId: fatherValue['siteId'],
      laboratoryId: fatherValue['laboratoryId'],
      diagnosisId: fatherValue['diagnosisId'],
      specialistId: fatherValue['specialistId'],
      userDataDescription: fatherValue['userDataDescription'],
      clinicalHistory: fatherValue['clinicalHistory'],
      descriptionDataSample: fatherValue['descriptionDataSample'],
      descriptionDataExam: fatherValue['descriptionDataExam'],
      consentForUseOfSamples: fatherValue['consentForUseOfSamples'],
      acmg: fatherValue['acmg'],
      mlpa: fatherValue['mlpa'],
      prioritization: fatherValue['prioritization']
    });

    this.toastService.setToast({
      title: 'Datos copiados',
      message: 'Verifique y complete los campos faltantes.',
      type: ToastTypeEnum.SUCCESS
    });
  }

  /** Para DUO/FUTUROS_PADRES: indica si el tipo de examen es exclusivo (solo un familiar) */
  get isExclusiveMode(): boolean {
    return this.examType === 'DUO' || this.examType === 'FUTUROS_PADRES';
  }

  /** Indica si el formulario del padre tiene datos ingresados */
  isFatherStarted(): boolean {
    const key = this.fatherForm?.get('idNumber')?.value;
    return !!key && String(key).trim().length > 0;
  }

  /** Indica si el formulario de la madre tiene datos ingresados */
  isMotherStarted(): boolean {
    const key = this.motherForm?.get('idNumber')?.value;
    return !!key && String(key).trim().length > 0;
  }

  /** En modo exclusivo, bloquea la madre si el padre ya tiene datos */
  get isMotherLocked(): boolean {
    return this.isExclusiveMode && this.isFatherStarted();
  }

  /** En modo exclusivo, bloquea el padre si la madre ya tiene datos */
  get isFatherLocked(): boolean {
    return this.isExclusiveMode && this.isMotherStarted();
  }

  /** Limpia un formulario para desbloquear el otro */
  clearForm(form: FormGroup, sex: string): void {
    form.reset();
    form.patchValue({ administrativeSex: sex, country: '' });
    if (this.examTypeId) {
      form.patchValue({ examTypeSelected: this.examTypeId });
    }
  }

  isFatherValid(): boolean {
    return this.fatherForm.valid;
  }

  isMotherValid(): boolean {
    return this.motherForm.valid;
  }

  isFatherReprocessChecked(): boolean {
    return this.fatherForm.get('isReprocess')?.value === true;
  }

  isMotherReprocessChecked(): boolean {
    return this.motherForm.get('isReprocess')?.value === true;
  }

  canSave(): boolean {
    if (this.examType === 'TRIO') {
      // TRIO requiere ambos formularios completos
      return this.isFatherValid() && this.isMotherValid();
    } else {
      // DUO y FUTUROS_PADRES requieren al menos uno de los dos
      return this.isFatherValid() || this.isMotherValid();
    }
  }

  get lgPadre(): string {
    const suffix = this.examType === 'FUTUROS_PADRES' ? 'FP' : 'P';
    return this.lgBase + suffix;
  }

  get lgMadre(): string {
    const suffix = this.examType === 'FUTUROS_PADRES' ? 'FM' : 'M';
    return this.lgBase + suffix;
  }

  get placerOrderNumberPadre(): string {
    return this.placerOrderNumber ?? '';
  }

  get placerOrderNumberMadre(): string {
    return this.placerOrderNumber ?? '';
  }

  saveFamilyMembers(): void {
    if (!this.canSave()) {
      return;
    }

    this.isLoading.set(true);

    const request: CreateFamilyMembersRequest = {
      lgBase: this.lgBase,
      examType: this.examType,
      orderId: this.orderId,
      placerOrderNumber: this.placerOrderNumber,
      fatherData:
        this.isFatherValid() && !this.isFatherLocked
          ? this.buildFamilyMemberData(this.fatherForm)
          : undefined,
      motherData:
        this.isMotherValid() && !this.isMotherLocked
          ? this.buildFamilyMemberData(this.motherForm)
          : undefined
    };

    this.familyMembersService.createFamilyMembers(request).subscribe({
      next: (response: CreateFamilyMembersResponse) => {
        this.isLoading.set(false);
        if (response.success) {
          this.toastService.setToast({
            title: 'Éxito',
            message: response.message,
            type: ToastTypeEnum.SUCCESS
          });
          this.saved.emit(response);
          this.close();
        } else {
          this.toastService.setToast({
            title: 'Error',
            message: response.message,
            type: ToastTypeEnum.ERROR
          });
        }
      },
      error: (error: Error) => {
        this.isLoading.set(false);
        this.toastService.setToast({
          title: 'Error',
          message:
            'Error al crear familiares: ' +
            (error.message || 'Error desconocido'),
          type: ToastTypeEnum.ERROR
        });
      }
    });
  }

  private buildFamilyMemberData(form: FormGroup): CreateFamilyMemberData {
    const value = form.value as Record<string, string | boolean | undefined>;
    return {
      identifierTypeCode: value['identifierTypeCode'] as string,
      idNumber: value['idNumber'] as string,
      givenName: value['givenName'] as string,
      middleName: value['middleName'] as string | undefined,
      familyName: value['familyName'] as string,
      maternalFamilyName: value['maternalFamilyName'] as string | undefined,
      administrativeSex: value['administrativeSex'] as string,
      birthDate: value['birthDate'] as string,
      maritalStatus: value['maritalStatus'] as string | undefined,
      race: value['race'] as string | undefined,
      emailAddress: value['emailAddress'] as string | undefined,
      telephoneNumber: value['telephoneNumber'] as string,
      streetAddress: value['streetAddress'] as string,
      city: value['city'] as string,
      state: value['state'] as string,
      country: value['country'] as string,
      specimenId: value['specimenId'] as string | undefined,
      specimenTypeId: value['specimenTypeId'] as string,
      specimenContainerId: value['specimenContainerId'] as string,
      priorityId: value['priorityId'] as string,
      examId: this.examTypeId,
      examTypeSelected: value['examTypeSelected'] as string | undefined,
      caseNumber: value['caseNumber'] as string | undefined,
      companyId: value['companyId'] as string,
      providerId: value['providerId'] as string,
      regionalId: value['regionalId'] as string,
      siteId: value['siteId'] as string,
      laboratoryId: value['laboratoryId'] as string,
      diagnosisId: value['diagnosisId'] as string,
      specialistId: value['specialistId'] as string | undefined,
      userDataDescription: value['userDataDescription'] as string | undefined,
      clinicalHistory: value['clinicalHistory'] as string | undefined,
      descriptionDataSample: value['descriptionDataSample'] as string | undefined,
      descriptionDataExam: value['descriptionDataExam'] as string | undefined,
      consentForUseOfSamples: value['consentForUseOfSamples'] as boolean | undefined,
      acmg: value['acmg'] as boolean | undefined,
      mlpa: value['mlpa'] as boolean | undefined,
      prioritization: value['prioritization'] as boolean | undefined,
      isReprocess: value['isReprocess'] as boolean | undefined,
      causeReprocessId: value['causeReprocessId'] as string | undefined,
      statusReprocessId: value['statusReprocessId'] as string | undefined,
      reprocessDescription: value['reprocessDescription'] as string | undefined
    };
  }

  getMaxDate(): string {
    const today = new Date();
    return today.toISOString().split('T')[0];
  }

  hasFieldError(form: FormGroup, field: string, error: string): boolean {
    const control = form.get(field);
    return !!(control?.hasError(error) && (control.touched || control.dirty));
  }

  isFieldInvalid(form: FormGroup, field: string): boolean {
    const control = form.get(field);
    return !!(control?.invalid && (control.touched || control.dirty));
  }
}
